// Generated by Claude Code
import i18n from 'i18next'
import { State } from '#core/state/State.ts'
import { ApiError } from '#core/api/Api.ts'
import CompanyAccessRequestsApi from '#core/api/auth/CompanyAccessRequestsApi.ts'
import CompanyAccessRequest from '#core/entities/auth/companies/access-requests/CompanyAccessRequest.ts'
import { showToast } from '#core/notifications/toast.ts'

const accessRequestsApi = new CompanyAccessRequestsApi()

export interface AccessRequestDecision {
  type: 'approve' | 'reject'
  id: string
  name: string
}

const toast = (key: string, variant: 'success' | 'warning') => showToast(i18n.t(key, { ns: 'settings' }), variant)

/** Access requests tab state; loads lazily so users who never open the tab never hit the endpoint. */
export class AccessRequestsState extends State<AccessRequestsState> {
  requests: CompanyAccessRequest[] = []
  loading = false
  loadError = false
  decision: AccessRequestDecision | null = null
  /** The role the approved member gets; empty for none. */
  roleId = ''
  _loaded = false

  constructor(key: string | undefined, storage: Storage | null) {
    super(key, storage)
  }

  filtered(query: string): CompanyAccessRequest[] {
    const q = query.toLowerCase().trim()
    if (!q) return this.requests
    return this.requests.filter(r =>
      r.displayName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    )
  }

  async ensureLoaded(): Promise<void> {
    if (this._loaded) return
    this._loaded = true
    await this.reload()
  }

  async reload(): Promise<void> {
    this.reactTo.loading = true
    this.reactTo.loadError = false
    try {
      const { results } = await accessRequestsApi.list()
      this.reactTo.requests = results.sort(CompanyAccessRequest.byPendingFirst)
    } catch {
      this.reactTo.loadError = true
    } finally {
      this.reactTo.loading = false
    }
  }

  requestDecision(type: AccessRequestDecision['type'], id: string): void {
    const request = this.requests.find(r => r.id === id)
    this.reactTo.roleId = ''
    this.reactTo.decision = { type, id, name: request?.displayName ?? '' }
  }

  cancelDecision(): void {
    this.reactTo.decision = null
  }

  /**
   * Runs the confirmed decision and reloads the list. A request someone else already decided (409) or
   * that no longer exists (404) only reloads; any other failure rejects so the dialog shows its error.
   */
  async executeDecision(onApproved: () => void): Promise<void> {
    const decision = this.decision
    if (!decision) return
    try {
      if (decision.type === 'approve') {
        await accessRequestsApi.approve(decision.id, this.roleId || null)
        onApproved()
        toast('usersTeams.accessRequests.toast.approved', 'success')
      } else {
        await accessRequestsApi.reject(decision.id)
        toast('usersTeams.accessRequests.toast.rejected', 'success')
      }
    } catch (e) {
      const status = e instanceof ApiError ? e.response?.status : undefined
      if (status !== 404 && status !== 409) throw e
      toast(status === 409 ? 'usersTeams.accessRequests.toast.alreadyDecided' : 'usersTeams.accessRequests.toast.notFound', 'warning')
    }
    await this.reload()
  }
}
